import type { SportItem, SportSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, ListHeader, ListToolbar, PrimaryCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, useNavigate, useParams, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PageTabs } from "../../components/common/page-tabs";
import { EventsPanel } from "../../components/events/events-panel";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { timestampColumn } from "../../components/common/list-columns";
import { StatusTag, gamesChipGroup } from "../../components/competition/list-chips";
import { canSetUpCompetition, sportsListSearch } from "../../components/sports/shared";
import { type SportFormTarget, SportFormModal } from "../../components/sports/sport-form-modal";
import { ApiError } from "../../lib/api/client";
import { requireAdminRole } from "../../lib/auth/guards";
import { gamesOptionsOptions, useDefaultGamesId } from "../../lib/competition/games";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { sortSelect } from "../../lib/list-page/sort-select";
import { usePanelSearch, tabbedSearch } from "../../lib/list-page/tab-search";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteSport, invalidateSports, sportStatsOptions, sportsListOptions } from "../../lib/sports/queries";

/** Settings › Sports — the sports of one games. `games_id` absent = the main games (`useDefaultGamesId`). */
export const Route = createFileRoute("/_app/settings/sports")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: tabbedSearch(["sports", "events"] as const),
	component: SportsShell,
});

/**
 * Sports | Events tabs (`?tab=`, absent = sports); `/settings/events` redirects here.
 * The drill-down ($sportId — its disciplines) nests under this route: when it matched, render it instead of the list. */
function SportsShell() {
	const params = useParams({ strict: false });
	const { t } = useTranslation("nav");
	const tab = Route.useSearch().tab ?? "sports";
	const navigate = Route.useNavigate();
	if (params.sportId !== undefined) return <Outlet />;
	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<PageTabs
				label={t("items.sports")}
				tabs={[
					{ key: "sports", label: t("items.sports") },
					{ key: "events", label: t("items.events") },
				]}
				current={tab}
				onChange={(next) => void navigate({ search: { tab: next === "sports" ? undefined : next } })}
			/>
			{tab === "events" ? <EventsPanel /> : <SportsPage />}
		</div>
	);
}

/** The filters as the URL holds them. `""` = not set; the URL drops empties. `games_id: ""` = the main games. */
interface SportFilters extends Record<string, unknown> {
	search: string;
	games_id: string;
	is_active: "" | "true" | "false";
}
const EMPTY_FILTERS: SportFilters = { search: "", games_id: "", is_active: "" };

function SportsPage() {
	const { t } = useTranslation("sports");
	const { t: tc } = useTranslation("common");
	const { session } = useRouteContext({ from: "/_app" });
	const { search, navigate } = usePanelSearch(sportsListSearch, "/settings/sports");
	const routerNavigate = useNavigate();
	const { formatNumber } = useUiStrings();
	const canWrite = canSetUpCompetition(session.active_role);
	const [form, setForm] = useState<SportFormTarget | null>(null);

	// What the drill-down carries back (its `list` param); left off when the list is at its defaults.
	const listBack = Object.values(search).some((v) => v !== undefined) ? search : undefined;

	const games = useQuery(gamesOptionsOptions());
	const gamesId = useDefaultGamesId(search.games_id);
	const defaultGamesId = useDefaultGamesId();
	const currentGames = games.data?.find((g) => g.games_id === gamesId);
	const noGames = games.isSuccess && games.data.length === 0;

	// No default sort in the URL: the API orders by (sort_order, code) — the programme order.
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => ({
			...sportsListOptions({
				...q,
				games_id: gamesId,
				is_active: search.is_active === undefined ? undefined : search.is_active ? "true" : "false",
			}),
			// Without a games the API would list every games' sports; wait for the default.
			enabled: gamesId !== undefined,
		}),
		columnsKey: "sports",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): SportFilters => ({
				search: s.search ?? "",
				games_id: s.games_id ?? "",
				is_active: s.is_active === undefined ? "" : s.is_active ? "true" : "false",
			}),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				games_id: f.games_id || undefined,
				is_active: f.is_active === "" ? undefined : f.is_active === "true",
			}),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	// One games' programme counts (the games is the view; search and status do not narrow them) — one aggregate statement.
	const stats = useQuery({ ...sportStatsOptions(gamesId ?? ""), enabled: gamesId !== undefined });

	const del = useConfirmDelete<SportItem>({
		remove: async (r) => {
			try {
				await deleteSport(r.sport_id, `Deleted sport ${r.code}`);
			} catch (err) {
				// 409 = still referenced (disciplines, para classes, cards): say so in our words.
				if (err instanceof ApiError && err.code === "CONFLICT") throw new Error(t("errors.inUse"));
				throw err;
			}
		},
		invalidate: invalidateSports,
		deleted: (r) => t("delete.deleted", { name: r.name }),
	});

	const total = page.list.data?.total;
	const sort = sortSelect<SportSortKey>(
		search,
		"sort_order:asc",
		[
			{ value: "sort_order:asc", label: t("sort.programme") },
			{ value: "sort_order:desc", label: t("sort.programmeDesc") },
			{ value: "code:asc", label: tc("sort.codeAsc") },
			{ value: "code:desc", label: tc("sort.codeDesc") },
			{ value: "name:asc", label: tc("sort.nameAsc") },
			{ value: "name:desc", label: tc("sort.nameDesc") },
			{ value: "created_at:desc", label: tc("sort.newest") },
			{ value: "created_at:asc", label: tc("sort.oldest") },
		],
		page.patch,
	);

	const columns: ColumnDef<SportItem, SportSortKey>[] = [
		{ key: "name", header: t("table.name"), sortable: true, lockVisible: true, minWidth: "14rem", cell: (r) => <PrimaryCell sub={r.code}>{r.name}</PrimaryCell> },
		{ key: "sort_order", header: t("table.sortOrder"), sortable: true, width: "5.5rem", numeric: true, cell: (r) => <span className="tabular-nums">{r.sort_order}</span> },
		{
			key: "is_active" as SportSortKey,
			header: t("table.status"),
			width: "7rem",
			cell: (r) => <StatusTag active={r.is_active} activeLabel={t("table.active")} inactiveLabel={t("table.inactive")} />,
		},
		{
			key: "disciplines_count" as SportSortKey,
			header: t("table.disciplines"),
			width: "7.5rem",
			numeric: true,
			cell: (r) =>
				r.disciplines_count > 0 ? (
					<Link
						to="/settings/sports/$sportId"
						params={{ sportId: r.sport_id }}
						search={{ list: listBack }}
						className="font-semibold tabular-nums text-gms-navy hover:underline"
						title={t("table.disciplinesTitle")}
					>
						{formatNumber(r.disciplines_count)}
					</Link>
				) : (
					<span className="text-base-content/30">{formatNumber(0)}</span>
				),
		},
		{
			key: "events_count" as SportSortKey,
			header: t("table.events"),
			width: "6.5rem",
			numeric: true,
			cell: (r) =>
				r.events_count > 0 ? (
					<Link
						to="/settings/sports"
						search={{ tab: "events", games_id: r.games_id, sport_id: r.sport_id }}
						className="font-semibold tabular-nums text-gms-navy hover:underline"
						title={t("table.eventsTitle")}
					>
						{formatNumber(r.events_count)}
					</Link>
				) : (
					<span className="text-base-content/30">{formatNumber(0)}</span>
				),
		},
		timestampColumn("created_at", t("table.created"), true),
	];

	// Picking a games is choosing the view, not narrowing it: an empty games
	// reads "no sports yet", and only search / status count as "filtered".
	const narrowed = search.search !== undefined || search.is_active !== undefined;
	const filteredEmpty = page.emptyState({
		filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
		none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
		clearLabel: t("empty.clearFilters"),
	});
	const empty = noGames
		? { title: t("empty.noGamesTitle"), description: t("empty.noGamesDescription") }
		: narrowed
			? filteredEmpty
			: { title: t("empty.noneTitle"), description: t("empty.noneDescription") };

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={<Trans t={t} i18nKey="page.intro" components={{ strong: <strong className="font-semibold text-gms-ink-2" /> }} />}
				actionLabel={t("page.addSport")}
				onAction={canWrite && currentGames ? () => setForm({ mode: "create", games: currentGames }) : undefined}
			/>

			<StatsStrip
				label={tc("list.summary")}
				cells={[
					{ key: "total", label: t("stats.total"), value: total ?? "—", note: narrowed ? t("stats.matching") : undefined },
					...(stats.data
						? [
								{ key: "active", label: t("stats.activeSports"), value: stats.data.sports_active },
								{ key: "disciplines", label: t("stats.activeDisciplines"), value: stats.data.disciplines_active },
								{ key: "events", label: t("stats.activeEvents"), value: stats.data.events_active },
							]
						: []),
				]}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.searchPlaceholder")}
				sort={sort}
				chips={[
					gamesChipGroup(games.data, gamesId, (id) => page.patch({ games_id: id === defaultGamesId ? undefined : id }), t("filters.gamesLabel")),
					{
						kind: "options",
						key: "status",
						label: t("filters.statusLabel"),
						options: [
							{ value: "true", label: t("filters.active"), count: stats.data?.sports_active },
							{ value: "false", label: t("filters.inactive"), count: stats.data?.sports_inactive },
						],
						value: search.is_active === undefined ? [] : [search.is_active ? "true" : "false"],
						onChange: (v) => page.patch({ is_active: v[0] === undefined ? undefined : v[0] === "true" }),
					},
				]}
				onClearAll={page.clearFilters}
				active={page.hasFilters}
			/>

			<DataTable<SportItem, SportSortKey>
				{...page.table}
				variant="list"
				// The list waits for the default games; show that wait as loading, not as "no sports".
				loading={page.table.loading || (gamesId === undefined && games.isPending)}
				columns={columns}
				rowKey={(r) => r.sport_id}
				empty={empty}
				rowActions={(r) => (
					<EditDeleteActions
						editLabel={t("table.editLabel", { name: r.name })}
						onEdit={canWrite ? () => setForm({ mode: "edit", sport: r }) : undefined}
						deleteLabel={t("table.deleteLabel", { name: r.name })}
						onDelete={canWrite ? () => del.ask(r) : undefined}
						// Para classes and cards are not counted on the row; the server's 409 covers them.
						canDelete={r.disciplines_count === 0 && r.events_count === 0}
						variant="list"
					>
						{/* The drill-down is read-only for non-writers, so everyone gets the eye. */}
						<IconButton
							label={t("table.viewLabel", { name: r.name })}
							icon={<Icons.Eye />}
							size="xs"
							variant="row"
							onClick={() => void routerNavigate({ to: "/settings/sports/$sportId", params: { sportId: r.sport_id }, search: { list: listBack } })}
						/>
					</EditDeleteActions>
				)}
			/>

			{canWrite && <SportFormModal target={form} onClose={() => setForm(null)} />}
			{canWrite && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={<Trans t={t} i18nKey="delete.description" values={{ name: del.row?.name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
